import { createClient, getUser } from "@/auth/client";
import type { User } from "@supabase/supabase-js";
import { ProfileData, ProfileFormData, UsageSummary } from "../types";

export class ProfileService {
  private static supabase = createClient();

  private static normalizeProfile(user: User, profile: Record<string, any> | null): ProfileData {
    const metadata = (user.user_metadata ?? {}) as Record<string, any>;

    const resolvedName =
      [
        profile?.name,
        profile?.full_name,
        profile?.fullName,
        profile?.username,
        metadata.full_name,
        metadata.fullName,
        metadata.name,
        metadata.display_name,
        metadata.displayName,
      ]
        .find((value) => typeof value === "string" && value.trim().length > 0)
        ?.trim() ?? null;

    const resolvedEmail = (profile?.email ?? user.email ?? null) as string | null;

    const resolvedAvatar =
      (profile?.avatar_url ??
        metadata.avatar_url ??
        metadata.avatar ??
        metadata.picture ??
        metadata.avatarUrl ??
        metadata.avatarURL ??
        null) as string | null;

    const facebookConnectedAt = (profile?.facebook_connected_at ?? null) as string | null;

    const facebookConnected =
      facebookConnectedAt ??
      profile?.facebook_user_id ??
      profile?.facebook_access_token ??
      profile?.facebook_full_name ??
      profile?.facebook_slug ??
      profile?.is_facebook_connected ??
      null;

    return {
      id: (profile?.id ?? user.id) as string,
      name: resolvedName,
      email: resolvedEmail,
      avatar_url: resolvedAvatar,
      facebook_connected_at: facebookConnectedAt,
      facebook_picture_url: (profile?.facebook_picture_url ?? null) as string | null,
      facebook_full_name: (profile?.facebook_full_name ?? null) as string | null,
      facebook_slug: (profile?.facebook_slug ?? null) as string | null,
      is_facebook_connected: Boolean(facebookConnected),
    };
  }

  static async fetchProfile(userId: string): Promise<{ data: ProfileData | null; error: string | null }> {
    try {
      const user = await getUser();
      if (!user) {
        return { data: null, error: "Not authenticated" };
      }

      const { data, error: profileError } = await this.supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return { 
          data: null, 
          error: profileError.message || "Failed to fetch profile data" 
        };
      }

      const normalizedProfile = ProfileService.normalizeProfile(user, data ?? null);

      return { data: normalizedProfile, error: null };
    } catch (err) {
      console.error("Unexpected error fetching profile:", err);
      return { data: null, error: "Unexpected error loading profile." };
    }
  }

  static async fetchUsageSummary(): Promise<{ data: UsageSummary | null; error: string | null }> {
    try {
      const response = await fetch("/api/user/usage-summary");

      if (!response.ok) {
        let message = "Failed to fetch usage summary.";
        try {
          const body = await response.json();
          if (body?.error && typeof body.error === "string") {
            message = body.error;
          }
        } catch (parseError) {
          // Ignore JSON parse errors and fall back to default message
        }
        return { data: null, error: message };
      }

      const payload = (await response.json()) as UsageSummary;
      return { data: payload, error: null };
    } catch (err) {
      console.error("Unexpected error fetching usage summary:", err);
      return { data: null, error: "Unexpected error loading usage summary." };
    }
  }

  static async updateProfile(userId: string, formData: ProfileFormData): Promise<{ success: boolean; error: string | null }> {
    try {
      const profilesQuery = this.supabase.from("profiles") as any;
      const { error: updateError } = await profilesQuery
        .update(formData)
        .eq("id", userId);

      if (updateError) {
        console.error("Error updating profile:", updateError);
        return { 
          success: false, 
          error: updateError.message || "Failed to update profile." 
        };
      }

      return { success: true, error: null };
    } catch (err) {
      console.error("Unexpected error during profile update:", err);
      return { success: false, error: "Unexpected error saving profile." };
    }
  }
} 